// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { Fragment, useMemo } from 'react'
import { Stage, Layer, Group, Circle } from 'react-konva'
import type Konva from 'konva'
import styles from './MaskStage.module.css'

interface MaskStageProps {
    stageRef: React.RefObject<Konva.Stage | null>
    maskLayerRef: React.RefObject<Konva.Layer | null>
    uiLayerRef: React.RefObject<Konva.Layer | null>
    maskContentGroupRef: React.RefObject<Konva.Group | null>
    uiContentGroupRef: React.RefObject<Konva.Group | null>
    containerSize: { w: number; h: number }
    imageRectInContainer: { x: number; y: number; w: number; h: number } | null
    showBrush: boolean
    brushCenter: { x: number; y: number } | null
    brushDiameterCssPx: number
    brushDiameterWorldPx: number
    brushEnabled: boolean
    contentScale: number
    contentOffset: { x: number; y: number }
    hasActiveImage?: boolean
    handlers: {
        onPointerDown: (e: any) => void
        onPointerMove: (e: any) => void
        onPointerUp: () => void
        onPointerLeave: () => void
    }
}

export function MaskStage(props: MaskStageProps): ReactElement {
    const {
        stageRef,
        maskLayerRef,
        uiLayerRef,
        maskContentGroupRef,
        uiContentGroupRef,
        containerSize,
        imageRectInContainer,
        showBrush,
        brushCenter,
        brushDiameterCssPx,
        brushDiameterWorldPx,
        brushEnabled,
        contentScale,
        contentOffset,
        hasActiveImage = false,
        handlers,
    } = props

    const width = containerSize.w
    const height = containerSize.h

    const setMaskLayerNode = (node: Konva.Layer | null) => {
        if (node) {
            ; (maskLayerRef as any).current = node
        }
    }
    const setUiLayerNode = (node: Konva.Layer | null) => {
        if (node) {
            ; (uiLayerRef as any).current = node
        }
    }

    const setMaskContentGroupNode = (node: Konva.Group | null) => {
        if (node) {
            ; (maskContentGroupRef as any).current = node
        }
    }
    const setUiContentGroupNode = (node: Konva.Group | null) => {
        if (node) {
            ; (uiContentGroupRef as any).current = node
        }
    }

    const listening = brushEnabled && !!imageRectInContainer

    const brushCircle = useMemo(() => {
        if (!showBrush || !brushCenter || brushDiameterCssPx <= 0) return null
        return (
            <Circle
                x={brushCenter.x}
                y={brushCenter.y}
                radius={brushDiameterWorldPx / 2}
                fill="rgba(255,10,92,0.5)"
                listening={false}
            />
        )
    }, [brushCenter, brushDiameterWorldPx, showBrush, brushDiameterCssPx])

    const showEmptyState = brushEnabled && !hasActiveImage

    return (
        <Fragment>
            <div className={styles.konvaOverlay}>
                {showEmptyState && (
                    <div className={styles.maskEmptyState}>
                        <span className={styles.maskEmptyStateText}>Select an image from the minimap</span>
                    </div>
                )}
                <Stage
                    ref={(node) => {
                        ; (stageRef as any).current = node as unknown as Konva.Stage
                    }}
                    width={width}
                    height={height}
                    onPointerDown={handlers.onPointerDown}
                    onPointerMove={handlers.onPointerMove}
                    onPointerUp={handlers.onPointerUp}
                    onPointerLeave={handlers.onPointerLeave}
                    listening={listening}
                >
                    <Layer ref={setMaskLayerNode as any} perfectDrawEnabled={false} listening={false}>
                        <Group
                            ref={setMaskContentGroupNode as any}
                            x={contentOffset.x}
                            y={contentOffset.y}
                            scaleX={contentScale}
                            scaleY={contentScale}
                            clipX={imageRectInContainer ? imageRectInContainer.x : 0}
                            clipY={imageRectInContainer ? imageRectInContainer.y : 0}
                            clipWidth={imageRectInContainer ? imageRectInContainer.w : 0}
                            clipHeight={imageRectInContainer ? imageRectInContainer.h : 0}
                            listening={false}
                        >
                            {/* Stroke lines are dynamically added to this group by the hook */}
                        </Group>
                    </Layer>
                    <Layer ref={setUiLayerNode as any} listening={false}>
                        <Group
                            ref={setUiContentGroupNode as any}
                            x={contentOffset.x}
                            y={contentOffset.y}
                            scaleX={contentScale}
                            scaleY={contentScale}
                            listening={false}
                        >
                            {brushCircle}
                        </Group>
                    </Layer>
                </Stage>
            </div>
        </Fragment>
    )
}

